import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton } from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';

import PoweredBy from '../ui/poweredBy';
import { getEventSectionsLeaderboard } from '../../features/leaderboard/leaderboardSlice';
import { filterSectionsByVenue } from '../../helpers/tvLogs';
import BSCircularProgress from '../ui/progress/circularProgress';
import SectionTotalsCard from '../ui/scoreboard/sectionTotalsCard';
import { useParams } from 'react-router-dom';

import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import { setEvent, setLoadingEvent } from '../../features/event/eventSlice';

import { useSearchParams } from 'react-router-dom';

const MAX_RETRIES = 3;
const RETRY_DELAY = 5000;
const SCROLL_SPEED = 0.5; // pixels per interval
const REFRESH_INTERVAL = 30000;
const PAUSE_DURATION = 5000;

// Scroll states
const SCROLL_STATES = {
  SCROLLING_DOWN: 'SCROLLING_DOWN',
  PAUSED_AT_BOTTOM: 'PAUSED_AT_BOTTOM',
  SCROLLING_UP: 'SCROLLING_UP',
  PAUSED_AT_TOP: 'PAUSED_AT_TOP'
};

const ForTVLogsScrolling = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { eventId } = useParams();

  const event = useSelector((state) => state.event.event);
  const loadingEvent = useSelector((state) => state.event.loadingEvent);
  const { mode, setMode } = useColorMode();
  const initialModeRef = useRef(mode);

  const loadingLeaderboard = useSelector((state) => state.leaderboard.loading);
  const sectionsLeaderboard = useSelector((state) => state.leaderboard.sectionLeaderboard);
  const venueFilter = String(searchParams.get('venue') || '').trim();
  const visibleSections = useMemo(
    () => filterSectionsByVenue(sectionsLeaderboard, venueFilter),
    [sectionsLeaderboard, venueFilter]
  );

  const totalPerPage = Number(searchParams.get('totalPerPage')) || visibleSections.length;
  const page = Number(searchParams.get('page')) || 1;

  const startIdx = (page - 1) * totalPerPage;
  const endIdx = startIdx + totalPerPage;

  const retryCount = useRef(0);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const stateTimeoutRef = useRef(null);

  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // All state read by the animation loop lives in refs. The loop is a
  // single long-lived requestAnimationFrame chain, started/stopped only
  // when the user toggles auto-scroll. It must never be torn down and
  // restarted as a side effect of data refreshes or re-renders, since
  // doing so previously cancelled the pending pause->resume timer before
  // it could fire, permanently getting the scroll stuck at the bottom.
  const scrollStateRef = useRef(SCROLL_STATES.SCROLLING_DOWN);
  // Tracks the intended scroll position with full float precision. We
  // never read `container.scrollTop` back for our own math - browsers
  // round/snap scrollTop to whole pixels, and with a sub-pixel
  // SCROLL_SPEED that rounding can eat the entire decrement each frame
  // (landing back on the same pixel), which made scrolling up appear to
  // do nothing while scrolling down happened to round the other way.
  const scrollPositionRef = useRef(0);
  const eventRef = useRef(event);
  useEffect(() => {
    eventRef.current = event;
  }, [event]);

  // Only used to show the initial loading spinner; background refreshes
  // (periodic or triggered when the scroll reaches the top) shouldn't
  // unmount the scrollable container, since that resets scroll position
  // and interrupts the animation loop.
  useEffect(() => {
    if (!loadingLeaderboard && visibleSections.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [loadingLeaderboard, visibleSections.length]);

  // Clear all timers
  const clearAllTimers = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
      stateTimeoutRef.current = null;
    }
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  // Refresh leaderboard data
  const refreshData = () => {
    if (eventRef.current) {
      dispatch(getEventSectionsLeaderboard({ eventId: eventRef.current.id }));
    }
  };

  // Main scroll loop using requestAnimationFrame. This function is only
  // ever kicked off once (see the effect below) and keeps re-scheduling
  // itself; it never gets recreated/restarted due to state or data
  // changes, so the pause->resume timers it schedules can never be
  // cancelled prematurely.
  const scrollLoop = () => {
    const container = containerRef.current;

    if (!container) {
      // Container isn't mounted yet (e.g. still loading) - keep polling.
      animationRef.current = requestAnimationFrame(scrollLoop);
      return;
    }

    const maxScrollTop = container.scrollHeight - container.clientHeight;

    // If content doesn't need scrolling
    if (maxScrollTop <= 10) {
      animationRef.current = requestAnimationFrame(scrollLoop);
      return;
    }

    switch (scrollStateRef.current) {
      case SCROLL_STATES.SCROLLING_DOWN:
        if (scrollPositionRef.current >= maxScrollTop - 5) {
          // Reached bottom
          scrollPositionRef.current = maxScrollTop;
          container.scrollTop = maxScrollTop;
          scrollStateRef.current = SCROLL_STATES.PAUSED_AT_BOTTOM;
          if (stateTimeoutRef.current) clearTimeout(stateTimeoutRef.current);
          stateTimeoutRef.current = setTimeout(() => {
            scrollStateRef.current = SCROLL_STATES.SCROLLING_UP;
          }, PAUSE_DURATION);
        } else {
          scrollPositionRef.current += SCROLL_SPEED;
          container.scrollTop = scrollPositionRef.current;
        }
        break;

      case SCROLL_STATES.SCROLLING_UP:
        if (scrollPositionRef.current <= 5) {
          // Reached top
          scrollPositionRef.current = 0;
          container.scrollTop = 0;
          scrollStateRef.current = SCROLL_STATES.PAUSED_AT_TOP;
          refreshData(); // Refresh data when reaching the top
          if (stateTimeoutRef.current) clearTimeout(stateTimeoutRef.current);
          stateTimeoutRef.current = setTimeout(() => {
            scrollStateRef.current = SCROLL_STATES.SCROLLING_DOWN;
          }, PAUSE_DURATION);
        } else {
          scrollPositionRef.current -= SCROLL_SPEED;
          container.scrollTop = scrollPositionRef.current;
        }
        break;

      case SCROLL_STATES.PAUSED_AT_BOTTOM:
      case SCROLL_STATES.PAUSED_AT_TOP:
        // Do nothing during pause
        break;

      default:
        break;
    }

    // Continue the loop
    animationRef.current = requestAnimationFrame(scrollLoop);
  };

  // Start/stop the scroll loop based on the enabled toggle only. This is
  // intentionally the *only* thing that starts/stops the animation loop -
  // it must not depend on leaderboard data, loading state, or scroll
  // state, since those change constantly (and independently of user
  // intent) and would otherwise tear down/restart the loop mid-pause.
  useEffect(() => {
    if (autoScrollEnabled) {
      animationRef.current = requestAnimationFrame(scrollLoop);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
        stateTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScrollEnabled]);

  // Set up periodic data refresh
  useEffect(() => {
    if (!autoScrollEnabled || !event) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    refreshIntervalRef.current = setInterval(refreshData, REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [autoScrollEnabled, event]);

  // Toggle auto-scroll
  const toggleAutoScroll = () => {
    setAutoScrollEnabled(!autoScrollEnabled);
    if (!autoScrollEnabled) {
      // Reset to starting position when enabling
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
      scrollPositionRef.current = 0;
      scrollStateRef.current = SCROLL_STATES.SCROLLING_DOWN;
      refreshData();
    } else {
      // Clear timers when disabling
      clearAllTimers();
    }
  };

  // Reset scroll position when data changes
  useEffect(() => {
    if (containerRef.current && autoScrollEnabled && visibleSections.length > 0) {
      const currentScroll = containerRef.current.scrollTop;
      if (currentScroll < 100) {
        containerRef.current.scrollTop = 0;
        scrollPositionRef.current = 0;
        scrollStateRef.current = SCROLL_STATES.SCROLLING_DOWN;
      }
    }
  }, [visibleSections, autoScrollEnabled]);

  useEffect(() => {
    if (!eventId) return;

    let isMounted = true;

    const loadEvent = () => {
      dispatch(setLoadingEvent(true));
      const docRef = doc(store, 'events', eventId);

      onSnapshot(
        docRef,
        (docSnap) => {
          if (!isMounted) return;
          if (docSnap.exists()) {
            const eventData = { id: docSnap.id, ...docSnap.data() };
            dispatch(setEvent(eventData));
            retryCount.current = 0;
          } else {
            console.log('No such document!');
            if (retryCount.current < MAX_RETRIES) {
              retryCount.current += 1;
              setTimeout(loadEvent, RETRY_DELAY);
            }
          }
          dispatch(setLoadingEvent(false));
        },
        (error) => {
          if (!isMounted) return;
          console.error('Error loading event:', error);
          if (retryCount.current < MAX_RETRIES) {
            retryCount.current += 1;
            setTimeout(loadEvent, RETRY_DELAY);
          }
          dispatch(setLoadingEvent(false));
        }
      );
    };

    loadEvent();

    return () => {
      isMounted = false;
    };
  }, [eventId, dispatch]);

  // Follow the device/OS colour scheme instead of forcing light mode, so the
  // TV log renders dark wherever the OS/browser is set to dark - and keeps
  // following it if the setting changes while the view is open.
  useEffect(() => {
    const previousMode = initialModeRef.current;
    const darkQuery =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const handleChange = (event) => {
      setMode(event.matches ? 'dark' : 'light');
    };

    if (darkQuery) {
      setMode(darkQuery.matches ? 'dark' : 'light');
      darkQuery.addEventListener('change', handleChange);
    }

    return () => {
      if (darkQuery) {
        darkQuery.removeEventListener('change', handleChange);
      }
      setMode(previousMode);
    };
  }, [setMode]);

  useEffect(() => {
    if (event) {
      dispatch(getEventSectionsLeaderboard({ eventId: event.id }));
    }
  }, [dispatch, event]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  return !event ? (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent="center"
      minHeight="100vh">
      <PoweredBy />
      <BSCircularProgress caption="Loading event..." size={40} />
    </Box>
  ) : (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      sx={{
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'background.default' }}>
        <PoweredBy />
      </Box>

      {/* Auto-scroll toggle button */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 15
        }}>
        <IconButton
          onClick={toggleAutoScroll}
          color="primary"
          size="large"
          sx={{
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'background.default'
            },
            boxShadow: 2
          }}>
          {autoScrollEnabled ? <Pause /> : <PlayArrow />}
        </IconButton>
      </Box>

      {!hasLoadedOnce && loadingLeaderboard ? (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} flex={1}>
          <BSCircularProgress caption={'Loading...'} size={30} />
        </Box>
      ) : visibleSections.length === 0 ? (
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          flex={1}
          color="text.secondary">
          {venueFilter
            ? `No sections are available for ${venueFilter}.`
            : 'No sections available.'}
        </Box>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: 2,
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              width: '100%'
            }}>
            {visibleSections.slice(startIdx, endIdx).map((section, index) => (
              <Box
                key={index}
                sx={{
                  width: '100%'
                }}>
                <SectionTotalsCard section={section} isTv={true} showReserves />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ForTVLogsScrolling;
